import React from 'react';
import { useDispatch } from 'react-redux';

import { useBoolean } from '@sravni/react-utils';

import { DEFAULT_FINGER_PRINT_VALUE } from '../../constants/fingerPrint';
import { COMMENTS_LIST_LIMIT, USER_MODERATOR_ROLES } from '../../constants/reviews';
import { IAnalyticsObject, sendCommentToReviewEvent } from '../../helpers/analyticsEvents';
import { ReviewType } from '../../helpers/reviews';
import { ICommentsListQuery } from '../../interfaces/reviews';
import { useComments, useCommentsList } from '../../store/hooks/comments';
import { useCurrentRegion } from '../../store/hooks/locations';
import { useFingerprint, useIsUserLogged, useUser, useUserInfo } from '../../store/hooks/users';
import { doCreateComment, doUpdateComment, fetchCommentsList } from '../../store/reducers/comments';

import styles from './comments-list.module.scss';
import { Comment } from './components/Comment';
import { ListHeading } from './components/ListHeading';
import { ShowMoreButton } from './components/ShowMoreButton';
import { getCommentCompany, removeCommentCompany } from './helpers';

export interface ICommentsListProps {
  reviewId: number;
  fetchComments: (params: ICommentsListQuery) => void;
  analyticLabel?: IAnalyticsObject;
  reviewTypes: ReviewType;
  logo?: string;
  organizationId: string;
  showAuthPopup: () => void;
  isBankAnswer?: boolean;
  scrollToComments?: () => void;
}

export const CommentsList: React.FC<ICommentsListProps> = ({
  reviewId,
  fetchComments,
  analyticLabel,
  reviewTypes,
  logo,
  organizationId,
  showAuthPopup,
  isBankAnswer,
  scrollToComments,
}) => {
  const dispatch = useDispatch();
  const fp = useFingerprint();
  const currentUser = useUser();
  const userInfo = useUserInfo();
  const isLoggedIn = useIsUserLogged();
  const comments = useComments();
  const commentsList = useCommentsList();
  const currentRegion = useCurrentRegion();

  const [showFullList, setShowFullList] = useBoolean(false);

  const currentUserId = currentUser?.sub;
  const isModerator = USER_MODERATOR_ROLES.some((role) => currentUser?.role?.includes(role));
  const isRepresentative = userInfo?.companyId?.toString() === organizationId.toString();

  const list = [...commentsList.list];
  const commentCompanyId = commentsList.commentCompanyId;
  const hasCompanyComment = commentCompanyId || comments[commentCompanyId]?.commentsCount > 0;
  const commentsCount = hasCompanyComment ? commentsList.total - 1 : commentsList.total;

  const updateCommentsList = async () => {
    await dispatch(
      fetchCommentsList({
        ReviewId: reviewId,
        PageSize: -1,
      }),
    );
  };

  const handleCreateComment = async (text: string, parentId?: number) => {
    try {
      await dispatch(
        doCreateComment({
          ...(parentId && { parentId }),
          text,
          reviewId: reviewId,
          fingerPrint: fp || DEFAULT_FINGER_PRINT_VALUE,
        }),
      );
      await updateCommentsList();
      sendCommentToReviewEvent(analyticLabel);
    } catch (e) {}
  };

  const handleUpdateComment = async (text: string, commentId: number, parentId: number) => {
    const data = {
      fingerPrint: fp,
      locationGarId: currentRegion.garId,
      id: commentId,
      parentId,
      reviewId,
      text,
      isMain: comments[commentId].isMain,
      ip: '',
    };

    await dispatch(doUpdateComment(data));
    await updateCommentsList();
  };

  const firstLevelCommentsCount = () => {
    const listTotal = commentsList.list.length;
    return hasCompanyComment ? listTotal - 1 : listTotal;
  };

  const commentsListWithoutCommentCompany = () => {
    if (hasCompanyComment) return removeCommentCompany(list, commentCompanyId);
    return commentsList.list;
  };

  const listToShow = () => {
    if (isBankAnswer) return getCommentCompany(list, commentCompanyId);

    return showFullList
      ? commentsListWithoutCommentCompany()
      : commentsListWithoutCommentCompany()?.slice(0, COMMENTS_LIST_LIMIT);
  };

  const moreAnswers = firstLevelCommentsCount() > 0 ? firstLevelCommentsCount() - COMMENTS_LIST_LIMIT : null;

  return (
    <section className={isBankAnswer ? '' : styles.commentsWrapper}>
      {!isBankAnswer && (
        <ListHeading
          currentUserId={currentUserId}
          isLoggedIn={isLoggedIn}
          totalComments={commentsCount}
          handleCreateComment={handleCreateComment}
          isRepresentative={isRepresentative}
          isModerator={isModerator}
          showAuthPopup={showAuthPopup}
        />
      )}
      {listToShow()?.length
        ? listToShow().map((key, index) => (
            <Comment
              key={`comment-${comments[key]?.id || index}`}
              reviewId={reviewId}
              comment={comments[key]}
              comments={comments}
              currentUserId={currentUserId}
              isLoggedIn={isLoggedIn}
              fetchComments={fetchComments}
              handleCreateComment={handleCreateComment}
              handleUpdateComment={handleUpdateComment}
              reviewTypes={reviewTypes}
              logo={logo}
              isRepresentative={isRepresentative}
              isModerator={isModerator}
              isBankAnswer={isBankAnswer}
              scrollToComments={scrollToComments}
            />
          ))
        : null}
      {!isBankAnswer && moreAnswers > 0 && !showFullList && (
        <ShowMoreButton onShowMore={() => setShowFullList.on()} moreAnswers={moreAnswers} />
      )}
    </section>
  );
};
